import DetectableOverflow from 'react-detectable-overflow';
import styled, { css } from 'styled-components';
import { zIndexes } from '@styles/zindex/zindex';
import Text, { TextProps } from '@components/Text/Text';
import React from 'react';
import { deviceTypes } from '@styles/mediaQueries';

export const Container = styled.div`
  display: grid;
  grid-template-rows: auto auto auto 1fr;
`;
Container.displayName = 'Container';

export const Content = styled.div`
  height: 100%;

  ::-webkit-scrollbar {
    display: none;
  }
`;

export const Header = styled.div`
  width: 100%;
  padding: 36px 40px 24px 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const Title = styled.div`
  color: #161a30;
  font-size: 18px;
  font-weight: 500;
`;

export const Buttons = styled.div`
  display: flex;
  align-items: center;
`;

export const FilterButton = styled.button`
  border-radius: 8px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #d8dae5;
  height: 36px;
  outline: 0;
  margin-right: 8px;
`;

export const AddButton = styled.button`
  border-radius: 8px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #d8dae5;
  height: 36px;
  outline: 0;
`;

export const AddButtonText = styled.div`
  margin-left: 10px;
  color: #474d66;
  font-size: 14px;
`;

export const Spacing = styled.div`
  margin: 0 40px;
`;

export const Divider = styled.div`
  width: 100%;
  background: #e6e8f0;
  height: 1px;
  margin-bottom: 8px;
`;

export const Heading = styled.div`
  /* Stick heading to top */
  top: 0;
  position: sticky;
  z-index: ${zIndexes.indexOf('table-heading')};
  background-color: white;

  width: 100%;
  display: grid;
  grid-template-columns: 4fr 100px 1fr 2fr 180px 30px;
  grid-row-gap: 0;
  grid-column-gap: 24px;
  padding-bottom: 8px;
`;

export const HeadingPrioritized = styled(Heading)`
  grid-template-columns: 55px 3fr 1fr 1fr 150px 180px 30px;
`;

export const Assignee = styled.img`
  height: 24px;
  width: 24px;
  object-fit: cover;
  border-radius: 12px;
`;

export const Direction = styled.div`
  margin-left: 8px;
  height: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ColumnContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  &.disabled {
    cursor: unset;
    user-select: unset;
  }
`;

export const ColumnName = styled.div`
  font-size: 12px;
  color: #8f95b2;
  font-weight: 500;
`;

export const Line = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 10px 0;
  background: #f5f7fb;
  border-radius: 8px;
  position: relative;
  cursor: pointer;

  & + & {
    margin-top: 12px;
  }
`;

export const LineInner = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 4fr 100px 1fr 2fr 180px;
  grid-row-gap: 0;
  grid-column-gap: 24px;
  background: #f5f7fb;
  border-radius: 8px;
  cursor: pointer;

  & + & {
    margin-top: 12px;
  }
`;

export const LineMenu = styled.div`
  width: 30px;
  margin-left: 24px;
`;

export const LinePrioritized = styled(Line)``;

export const LoadingLine = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  background: #fff;
  width: 100%;
  height: 100%;
  z-index: 1;
  border-radius: 8px;
  opacity: 0.5;
`;

export const LineInnerPrioritized = styled(LineInner)`
  grid-template-columns: 55px 3fr 1fr 1fr 150px 180px;
  min-width: 0;
  min-height: 0;
`;

interface ILineText {
  limitOfLines?: number;
}

export const LineText = styled(DetectableOverflow)<ILineText>`
  color: #1b203a;
  font-size: 14px;

  ${({ limitOfLines }) =>
    !!limitOfLines &&
    css`
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: ${limitOfLines}; /* number of lines to show */
      line-clamp: ${limitOfLines};
      -webkit-box-orient: vertical;
    `};
`;

export const LineItem = styled.div`
  display: flex;
  align-items: center;

  min-width: 0;
  overflow: hidden;

  &:first-child {
    padding-left: 12px;
  }

  &:last-child {
    padding-right: 12px;
  }
`;

export const FlagContainer = styled.div`
  height: 20px;
`;

export const NoText = styled.div`
  color: #c1c4d6;
  font-size: 14px;
  font-style: italic;
`;

export const InputContainer = styled.div`
  margin-right: 8px;
  display: flex;
  align-items: center;
  position: relative;
`;

interface IInputSearch {
  search?: boolean;
  clear?: boolean;
}

export const InputSearch = styled.input<IInputSearch>`
  border-radius: 8px;
  padding-left: 32px;
  padding-right: 16px;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #d8dae5;
  height: 36px;
  color: #474d66;
  font-size: 14px;
  width: 250px;

  &::placeholder {
    color: #8f95b2;
  }

  ${({ search }) =>
    search &&
    css`
      padding-right: 66px;
    `}
  ${({ clear }) =>
    clear &&
    css`
      padding-right: 36px;
    `}
`;

export const SearchIconContainer = styled.div`
  position: absolute;
  left: 12px;
`;

export const SearchClear = styled.div`
  position: absolute;
  right: 16px;
  cursor: pointer;
`;

export const SearchButton = styled.div`
  position: absolute;
  right: 12px;
  font-size: 12px;
  color: #1371fe;
  cursor: pointer;
`;

export const AffectsContiner = styled.div`
  display: flex;
  align-items: center;
`;

export const AffectsNumber = styled.div`
  font-weight: 500;
  margin-left: 8px;
  font-size: 14px;
  color: #1371fe;
`;

export const TagsRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;
  column-gap: 8px;

  [data-device='${deviceTypes.mobile}'] & {
    .TagsList_responsive & {
      column-gap: 4px;
    }
  }
`;

export const TagsRight = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0) 74%,
    var(--parent-bg, #f5f7fb) 97%
  );
`;

export const TagLine = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;

  /* This is for AffectedItems, ProductsList dialog to look okay. */
  display: grid;
`;

export const TagContainer = styled(Text)`
  /* height: 30px; */
  border-radius: 15px;
  padding: 4px 12px;

  /* font-size: 14px;
  font-weight: 500; */
  /* display: flex; */
  /* align-items: center; */
  width: fit-content;
  white-space: nowrap;

  /* & + & {
    margin-left: 8px;
  } */
` as React.FC<TextProps>;

export const ManufacturerImage = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  object-fit: cover;
  background: transparent;
  &.defaultImage {
    border: none;
  }
`;

export const DetailsDialogButton = styled.button`
  display: flex;
  width: 100%;
  border: 0;
  background-color: transparent;
`;

export const Internal = styled.div`
  color: #1371fe;
  font-weight: 500;
`;
